import { FC, useCallback, useEffect, useContext, useState, useMemo } from 'react';
import { Box, Button, Typography, Select } from '@robocorp/components';
import { useTranslation } from 'react-i18next';
import { VariablesContext } from '../context';
import { useActions, useSelector } from '../../../../hooks';
import { CurrentWorkspaceRestriction } from '~/components';
import { mapInternalEndpointPermissions } from '~/types/WorkspaceKeyTypes';

export const SelectApiKey: FC = () => {
  const [key, setKey] = useState<string | undefined>(undefined);
  const { endpoint, variables, permission, setVariables } = useContext(VariablesContext);
  const { t } = useTranslation();

  const {
    creatingWorkspaceKey,
    dialogOpen,
    loadingWorkspaceKey,
    loadingWorkspaceKeys,
    workspaceKeyById,
  } = useSelector(({ entityStates, ui, workspaceResources }) => ({
    loadingWorkspaceKeys: entityStates.workspaceKeys.loading,
    creatingWorkspaceKey: entityStates.workspaceKeys.creating,
    loadingWorkspaceKey: entityStates.workspaceKeys.byId,
    dialogOpen: ui.wsKeyDialogState.dialogOpen,
    workspaceKeyById: workspaceResources.workspaceKeys.byId,
  }));

  const { getWorkspaceKey, getWorkspaceKeys, toggleWsKeyDialog } = useActions(
    ({ uiActions, workspaceKeyActions }) => ({
      getWorkspaceKey: workspaceKeyActions.getWorkspaceKey,
      getWorkspaceKeys: workspaceKeyActions.getWorkspaceKeys,
      toggleWsKeyDialog: uiActions.toggleWsKeyDialog,
    }),
  );

  const apiKeysItems = useMemo(() => {
    const endpointPermissions = mapInternalEndpointPermissions([permission as any]);
    return Object.values(workspaceKeyById)
      .filter(
        (item) =>
          endpointPermissions &&
          endpointPermissions.every((endpointPermission) =>
            item.permissions.includes(endpointPermission),
          ),
      )
      .map(({ name, id }) => ({ label: name, value: id }));
  }, [permission, workspaceKeyById]);

  useEffect(() => {
    getWorkspaceKeys();
  }, []);

  useEffect(() => {
    if (permission) {
      const keySet = Object.values(workspaceKeyById).find(
        (item) => item.id === key && item.permissions.includes(permission as any),
      );
      if (keySet) {
        return;
      }

      const keyExists = Object.values(workspaceKeyById).find((item) =>
        item.permissions.includes(permission as any),
      );
      if (keyExists && keyExists.id !== key) {
        setKey(keyExists.id);
        if (!keyExists.secret) {
          getWorkspaceKey(keyExists.id);
        } else {
          setVariables({ apiKey: keyExists.secret });
        }
      }
    }
  }, [permission, workspaceKeyById]);

  useEffect(() => {
    if (key) {
      const workspaceKey = workspaceKeyById[key];
      if (workspaceKey?.secret && variables.apiKey !== workspaceKey.secret) {
        setVariables({ apiKey: workspaceKey.secret });
      }
    }
  }, [workspaceKeyById]);

  const onChange = useCallback(
    (value: string) => {
      const workspaceKey = workspaceKeyById[value];

      setKey(value);

      if (workspaceKey?.secret && variables.apiKey !== workspaceKey.secret) {
        setVariables({ apiKey: workspaceKey?.secret });
      }

      if (!workspaceKey?.secret) {
        getWorkspaceKey(value);
        setVariables({ apiKey: undefined });
      }
    },
    [setVariables, workspaceKeyById],
  );

  const onCreateWorkspaceKey = useCallback(() => {
    toggleWsKeyDialog(
      undefined,
      permission ? mapInternalEndpointPermissions([permission as any]) : undefined,
    );
  }, [endpoint]);

  if (typeof key === 'string' && loadingWorkspaceKey[key]?.loading) {
    return (
      <Box mb={20}>
        <Select
          label={t('APIHelper.chooseKey')}
          items={[]}
          disabled
          placeholder={t('APIHelper.requestingKey')}
        />
      </Box>
    );
  }

  return (
    <Box mb={20}>
      {apiKeysItems.length === 0 && !loadingWorkspaceKeys && !creatingWorkspaceKey ? (
        <>
          <Typography mb={12} color="grey20">
            {t('APIHelper.noKeyWithPermission', { permission })}
          </Typography>
          <CurrentWorkspaceRestriction permission="write_workspace_keys">
            <Button onClick={onCreateWorkspaceKey} disabled={dialogOpen}>
              {t('APIHelper.createKey')}
            </Button>
          </CurrentWorkspaceRestriction>
        </>
      ) : (
        <Select
          label={t('APIHelper.chooseKey')}
          searchable
          items={apiKeysItems}
          onChange={onChange}
          disabled={loadingWorkspaceKeys || creatingWorkspaceKey}
          value={key}
          placeholder={
            loadingWorkspaceKeys || creatingWorkspaceKey ? t('loading') : `${t('choose')}...`
          }
        />
      )}
    </Box>
  );
};
